import { pageTitle } from '@palaccount/config';
import type { PartnerStatement, StatementSide } from '@palaccount/contracts';
import { AGING_BANDS, PAGE_SIZE_ALL } from '@palaccount/contracts';
import { Hourglass, Info, Send } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Amount, Ltr } from '@/components/amount';
import { Pager } from '@/components/pager';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DocumentActions } from '@/features/documents/document-actions';
import { useSendStatement } from '@/features/partners/api';
import { usePartnerPicker } from '@/features/partners/partner-picker';
import { PartnerTypeahead } from '@/features/partners/partner-typeahead';
import { usePagedState, usePageRange } from '@/lib/paging';
import { localizeServerMessage } from '@/lib/server-messages';
import { cn } from '@/lib/utils';

import { useStatementReport } from './api';
import { ReportsTabs } from './reports-tabs';

/**
 * CRM-03 — Customer / Supplier statement, as a REPORT CENTRE.
 *
 * The same statement CRM-04 draws on the partner detail (`partner-statement-
 * card.tsx`), with the one thing that screen never needed: a partner picker
 * (BR-CRM-04-01 — "exactly one partner at a time, chosen from the partners
 * list, searchable by name or code"). Everything the card says about what the
 * browser must NOT compute holds here word for word — every figure is the
 * server's, off `GET /api/reports/statement`, which is `partnerStatement()`
 * itself (one builder, one closing balance).
 *
 * What this screen adds over the card, and only this:
 *  - the partner picker, the view select when the partner is both;
 *  - "Include drafts", off by default (R9 is the default; the switch is a
 *    what-if read, and the aging bands ignore it — BR-CRM-04-20);
 *  - a currency view for a foreign-currency partner: the partner's columns,
 *    the base columns at each document's FROZEN rate (EC-19-10), or both.
 *    A base-currency partner never sees the control (GEN-116).
 */

/** §5e's five colours: "green → yellow → orange → deep orange → red". */
const BAND_BAR: Record<string, string> = {
  current: 'bg-green-500',
  d1_30: 'bg-amber-400',
  d31_60: 'bg-orange-500',
  d61_90: 'bg-orange-700',
  d90_plus: 'bg-red-600',
};

type CurrencyView = 'both' | 'partner' | 'base';

/** BR-CRM-04-15 — the code links to its own document, by the row's kind. */
function statementDocPath(row: PartnerStatement['rows'][number]): string {
  switch (row.kind) {
    case 'invoice':
      return `/sales/${row.id}`;
    case 'receipt':
      return `/accounting/receipts/${row.id}`;
    case 'credit_note':
      return `/sales/returns/${row.id}`;
    case 'purchase_invoice':
      return `/purchases/bills/${row.id}`;
    case 'payment':
      return `/accounting/payments/${row.id}`;
    case 'purchase_return':
      return `/purchases/returns/${row.id}`;
  }
}

/** BR-CRM-04-12: which side a row carries is its KIND, never a zero-test. */
function isDebitRow(kind: PartnerStatement['rows'][number]['kind']): boolean {
  return kind === 'invoice' || kind === 'payment' || kind === 'purchase_return';
}

function SummaryCard({
  label,
  caption,
  value,
  currency,
  emphasis,
}: {
  label: string;
  caption: string;
  value: string;
  currency: string;
  emphasis?: boolean;
}) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn('text-lg font-bold', emphasis && 'text-primary')}>
        <Amount value={value} currency={currency} />
      </p>
      <p className="text-xs text-muted-foreground">{caption}</p>
    </div>
  );
}

const Dash = () => (
  <span aria-hidden className="text-muted-foreground">
    —
  </span>
);

export function StatementReportPage() {
  const { t, i18n } = useTranslation();
  const [partnerId, setPartnerId] = useState<string | null>(null);
  const picker = usePartnerPicker(partnerId);
  const partner = partnerId ? picker.byId.get(partnerId) : undefined;

  const [side, setSide] = useState<StatementSide | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [currencyView, setCurrencyView] = useState<CurrencyView>('both');
  /** Which currency's statement (owner decision, 30 Oct 2026); '' = the partner's own. */
  const [stCurrency, setStCurrency] = useState('');
  const paged = usePagedState(50, 'statement');
  const { page, pageSize, setPage, change } = paged;

  useEffect(() => {
    document.title = pageTitle(t('reports.statementTitle'));
  }, [t]);

  const filters = {
    side: side || undefined,
    from: from || undefined,
    to: to || undefined,
    q: q || undefined,
    currency: stCurrency || undefined,
  };
  const { data: statement, isError } = useStatementReport(
    partnerId ? { partnerId, ...filters } : null,
    page,
    pageSize,
  );
  const range = usePageRange(statement, pageSize);

  /**
   * THE PRINTABLE LEDGER — the whole account, exactly as the partner card
   * argues it (`UNPAGED_CALLERS`, route `GET /api/reports/statement`): Print
   * and PDF must carry the same closing balance, and that is the account's,
   * not the page's. Enabled only when the ledger outgrows the page.
   */
  const needsFullLedger = statement !== undefined && statement.total > statement.rows.length;
  const { data: fullLedger } = useStatementReport(
    needsFullLedger && partnerId ? { partnerId, ...filters } : null,
    1,
    PAGE_SIZE_ALL,
  );
  const printable = needsFullLedger ? fullLedger : statement;

  // BR-CRM-04-17's confirmation and its 2.6-second clear, as on the card.
  const sendStatement = useSendStatement(partnerId ?? '');
  const [emailedTo, setEmailedTo] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const clearTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(clearTimer.current), []);
  const onSendStatement = () => {
    setSendError(null);
    sendStatement.mutate(statement?.currency, {
      onSuccess: (result) => {
        setEmailedTo(result.emailedTo);
        clearTimeout(clearTimer.current);
        clearTimer.current = setTimeout(() => setEmailedTo(null), 2600);
      },
      onError: (error) => {
        setEmailedTo(null);
        setSendError(
          localizeServerMessage(t, error instanceof Error ? error.message : String(error), error),
        );
      },
    });
  };

  const onPickPartner = (id: string | null) => {
    setPartnerId(id);
    // A new partner is a new account: its own natural view and window.
    setSide('');
    setFrom('');
    setTo('');
    setCurrencyView('both');
    setStCurrency('');
    setPage(1);
  };

  const currency = statement?.currency ?? partner?.currency ?? '';
  const base = statement?.baseCurrency ?? '';
  /** GEN-116: a base-currency partner has no second currency to show. */
  const foreign = statement !== undefined && statement.currency !== statement.baseCurrency;
  const showPartnerCols = !foreign || currencyView !== 'base';
  const showBaseCols = foreign && currencyView !== 'partner';
  const shown: StatementSide =
    statement?.side ?? (partner && !partner.isCustomer ? 'supplier' : 'customer');

  const bands = statement?.aging ?? AGING_BANDS.map((band) => ({ band, amount: '0.00' }));
  const bandTotal = bands.reduce((sum, band) => sum + Number(band.amount), 0);
  const overdue = Number(statement?.summary?.overdueAmount ?? '0');
  const moneyCols = (showPartnerCols ? 2 : 0) + (showBaseCols ? 2 : 0);

  return (
    <div className="grid gap-4">
      <ReportsTabs />
      <div className="no-print flex flex-wrap items-center gap-3">
        <h2 className="me-auto text-lg font-bold">{t('reports.statementTitle')}</h2>
        {partnerId && partner ? (
          <>
            {emailedTo ? (
              <span role="status" className="text-sm font-medium text-green-700 dark:text-green-400">
                {t('partners.statementEmailed', { address: emailedTo })}
              </span>
            ) : null}
            {sendError ? (
              <span role="alert" className="text-sm text-destructive">
                {sendError}
              </span>
            ) : null}
            <DocumentActions
              request={{
                kind: 'partner_statement',
                locale: i18n.language.startsWith('ar') ? 'ar' : 'en',
                partnerId,
                side: shown,
                ...(statement
                  ? { from: statement.from, to: statement.to, currency: statement.currency }
                  : {}),
              }}
              payload={
                printable
                  ? {
                      kind: 'partner_statement',
                      data: {
                        side: shown,
                        partnerCode: partner.code,
                        partnerName: partner.name,
                        currency: printable.currency,
                        asOf: printable.to,
                        openingBalance: printable.summary.openingBalance,
                        rows: printable.rows,
                        closingBalance: printable.summary.closingBalance,
                      },
                    }
                  : null
              }
            />
            <Button variant="outline" size="sm" disabled={sendStatement.isPending} onClick={onSendStatement}>
              <Send aria-hidden /> {t('partners.sendStatement')}
            </Button>
          </>
        ) : null}
      </div>

      {/* The selection panel — the one card the partner screen does not have. */}
      <Card className="no-print">
        <CardContent className="flex flex-wrap items-end gap-4">
          <div className="grid min-w-64 gap-1">
            <PartnerTypeahead
              entity="customer"
              id="statement-partner"
              label={t('reports.stPartner')}
              placeholder={t('partners.searchPh')}
              value={partnerId}
              onChange={onPickPartner}
              options={picker.options}
              selected={picker.optionFor(partnerId)}
              query={picker.query}
              onQueryChange={picker.setQuery}
              loading={picker.loading}
            />
          </div>
          {/* BR-CRM-04-03: the view is offered only when the partner is both. */}
          {statement?.canToggle ? (
            <div className="grid gap-1">
              <Label htmlFor="statement-side">{t('partners.stView')}</Label>
              <NativeSelect
                id="statement-side"
                value={side || statement.side}
                onChange={(event) => change(setSide)(event.target.value as StatementSide)}
                className="h-10 w-auto text-sm"
              >
                <option value="customer">{t('partners.stView_customer')}</option>
                <option value="supplier">{t('partners.stView_supplier')}</option>
              </NativeSelect>
            </div>
          ) : null}
          <div className="grid gap-1">
            <Label htmlFor="statement-from">{t('partners.stFrom')}</Label>
            <Input
              id="statement-from"
              type="date"
              dir="ltr"
              className="w-40"
              value={from || statement?.from || ''}
              onChange={(event) => change(setFrom)(event.target.value)}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="statement-to">{t('partners.stTo')}</Label>
            <Input
              id="statement-to"
              type="date"
              dir="ltr"
              className="w-40"
              value={to || statement?.to || ''}
              onChange={(event) => change(setTo)(event.target.value)}
            />
          </div>
          {/* One statement per currency (owner decision, 30 Oct 2026): drawn
              only when the partner has documents in more than one. */}
          {statement && statement.currencies.length > 1 ? (
            <div className="grid gap-1">
              <Label htmlFor="statement-doc-currency">{t('reports.stStatementCurrency')}</Label>
              <NativeSelect
                id="statement-doc-currency"
                dir="ltr"
                value={statement.currency}
                onChange={(event) => {
                  change(setStCurrency)(event.target.value);
                  setCurrencyView('both');
                }}
                className="h-10 w-auto text-sm"
              >
                {statement.currencies.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </NativeSelect>
            </div>
          ) : null}
          {/* GEN-116: drawn only when there IS a second currency to show. */}
          {foreign ? (
            <div className="grid gap-1">
              <Label htmlFor="statement-currency">{t('reports.stCurrency')}</Label>
              <NativeSelect
                id="statement-currency"
                value={currencyView}
                onChange={(event) => setCurrencyView(event.target.value as CurrencyView)}
                className="h-10 w-auto text-sm"
              >
                <option value="both">{t('reports.stCurrencyBoth')}</option>
                <option value="partner">{t('reports.stCurrencyPartner', { currency })}</option>
                <option value="base">{t('reports.stCurrencyBase', { currency: base })}</option>
              </NativeSelect>
            </div>
          ) : null}
          <Input
            type="search"
            aria-label={t('partners.stSearch')}
            placeholder={t('partners.stSearch')}
            value={q}
            onChange={(event) => change(setQ)(event.target.value)}
            className="max-w-xs"
            disabled={!partnerId}
          />
        </CardContent>
      </Card>

      {!partnerId ? (
        <Card>
          <CardContent>
            <p className="py-12 text-center text-sm text-muted-foreground">{t('reports.stPickPartner')}</p>
          </CardContent>
        </Card>
      ) : isError ? (
        <p role="alert" className="py-8 text-center text-sm text-destructive">
          {t('common.loadFailed')}
        </p>
      ) : (
        <>
          {/* F-27's exclusion is said by the statement-currency select above:
              every currency left out of this statement is an option there, so
              the server's `excludedCurrencies` sentences are not repeated. */}

          {/* §5d's five summary figures. */}
          <Card>
            <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              <SummaryCard
                label={t('partners.stOpening')}
                caption={t('partners.stAsOf', { date: statement?.from ?? '' })}
                value={statement?.summary?.openingBalance ?? '0.00'}
                currency={currency}
              />
              <SummaryCard
                label={t('partners.stTotalDebits')}
                caption={t('partners.stThisPeriod')}
                value={statement?.summary?.totalDebits ?? '0.00'}
                currency={currency}
              />
              <SummaryCard
                label={t('partners.stTotalCredits')}
                caption={t('partners.stThisPeriod')}
                value={statement?.summary?.totalCredits ?? '0.00'}
                currency={currency}
              />
              <SummaryCard
                label={t('partners.stClosing')}
                caption={t('partners.stPeriodTo', { date: statement?.to ?? '' })}
                value={statement?.summary?.closingBalance ?? '0.00'}
                currency={currency}
                emphasis
              />
              {/* BR-CRM-04-08: red when anything is overdue, green when nothing is. */}
              <div
                data-testid="overdue-tile"
                className={cn(
                  'rounded-lg border p-3',
                  overdue > 0
                    ? 'border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950'
                    : 'border-green-300 bg-green-50 dark:border-green-900 dark:bg-green-950',
                )}
              >
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t('partners.stOverdue')}
                </p>
                <p
                  className={cn(
                    'text-lg font-bold',
                    overdue > 0 ? 'text-red-700 dark:text-red-300' : 'text-green-700 dark:text-green-300',
                  )}
                >
                  <Amount value={statement?.summary?.overdueAmount ?? '0.00'} currency={currency} />
                </p>
                <p className="text-xs text-muted-foreground">
                  {overdue > 0 ? t('partners.stThisPeriod') : t('partners.stNothingOverdue')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* §5e's aging breakdown — posted, open documents only, whatever the drafts switch says. */}
          <Card>
            <CardHeader className="flex-row items-center gap-2 space-y-0">
              <Hourglass className="size-4 text-muted-foreground" aria-hidden />
              <CardTitle>{t('partners.stAging')}</CardTitle>
              <span className="text-sm text-muted-foreground">{t('partners.stAgingSub')}</span>
            </CardHeader>
            <CardContent className="grid gap-4">
              <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
                {bandTotal > 0
                  ? bands
                      .filter((band) => Number(band.amount) > 0)
                      .map((band) => (
                        <div
                          key={band.band}
                          data-testid={`aging-seg-${band.band}`}
                          className={BAND_BAR[band.band]}
                          style={{ width: `${(Number(band.amount) / bandTotal) * 100}%` }}
                        />
                      ))
                  : null}
              </div>
              <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {bands.map((band) => {
                  const empty = Number(band.amount) === 0;
                  return (
                    <div key={band.band} data-testid={`aging-band-${band.band}`} className="grid gap-1">
                      <span className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                        <span
                          aria-hidden
                          className={cn('size-2 rounded-full', empty ? 'bg-muted' : BAND_BAR[band.band])}
                        />
                        {t(`partners.stBand_${band.band}`)}
                      </span>
                      <span className={cn('text-sm font-semibold', empty && 'text-muted-foreground')}>
                        <Amount value={band.amount} currency={currency} />
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* The transaction ledger. */}
          <Card>
            <CardHeader>
              <CardTitle>{t('partners.stLedger')}</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4">
              {statement?.conversionNote ? (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Info className="size-3.5 shrink-0" aria-hidden />
                  {statement.conversionNote}
                </p>
              ) : null}
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('common.date')}</TableHead>
                    <TableHead>{t('partners.stKindCol')}</TableHead>
                    <TableHead>{t('partners.stCode')}</TableHead>
                    <TableHead>{t('partners.stReference')}</TableHead>
                    {showPartnerCols ? (
                      <>
                        <TableHead className="text-end">{t('partners.stDebit')}</TableHead>
                        <TableHead className="text-end">{t('partners.stCredit')}</TableHead>
                      </>
                    ) : null}
                    {showBaseCols ? (
                      <>
                        <TableHead className="text-end">{t('partners.stDebitBase', { currency: base })}</TableHead>
                        <TableHead className="text-end">{t('partners.stCreditBase', { currency: base })}</TableHead>
                      </>
                    ) : null}
                    <TableHead className="text-end">{t('partners.stBalance')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {/* §5f's opening row — it stays even when the search empties the body. */}
                  <TableRow className="bg-blue-50/60 dark:bg-blue-950/40">
                    <TableCell colSpan={3} className="font-medium">
                      {t('partners.stOpening')}
                    </TableCell>
                    <TableCell />
                    {Array.from({ length: moneyCols }, (_, index) => (
                      <TableCell key={index} className="text-end">
                        <Dash />
                      </TableCell>
                    ))}
                    <TableCell className="text-end font-semibold">
                      <Amount value={statement?.summary?.openingBalance ?? '0.00'} />
                    </TableCell>
                  </TableRow>
                  {statement && statement.rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5 + moneyCols} className="py-6 text-center text-sm text-muted-foreground">
                        {t('partners.stNoRows')}
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {(statement?.rows ?? []).map((row) => {
                    const debit = isDebitRow(row.kind);
                    return (
                      <TableRow key={row.id}>
                        <TableCell>
                          <Ltr value={row.date} className="font-mono" />
                        </TableCell>
                        <TableCell>{t(`partners.stKind.${row.kind}`)}</TableCell>
                        <TableCell>
                          <Link to={statementDocPath(row)} className="underline-offset-2 hover:underline">
                            <Ltr value={row.code} className="font-mono" />
                          </Link>
                        </TableCell>
                        <TableCell className="max-w-64 truncate text-muted-foreground">{row.reference ?? ''}</TableCell>
                        {showPartnerCols ? (
                          <>
                            <TableCell className="text-end">{debit ? <Amount value={row.debit} /> : <Dash />}</TableCell>
                            <TableCell className="text-end">{debit ? <Dash /> : <Amount value={row.credit} />}</TableCell>
                          </>
                        ) : null}
                        {showBaseCols ? (
                          <>
                            {/* EC-19-10: each row at the rate ITS document froze; no rate, no figure. */}
                            <TableCell className="text-end">
                              {!debit ? (
                                <Dash />
                              ) : row.debitBase === null ? (
                                <span className="text-muted-foreground" title={t('partners.stNoRate')}>
                                  —
                                </span>
                              ) : (
                                <Amount value={row.debitBase} currency={base} />
                              )}
                            </TableCell>
                            <TableCell className="text-end">
                              {debit ? (
                                <Dash />
                              ) : row.creditBase === null ? (
                                <span className="text-muted-foreground" title={t('partners.stNoRate')}>
                                  —
                                </span>
                              ) : (
                                <Amount value={row.creditBase} currency={base} />
                              )}
                            </TableCell>
                          </>
                        ) : null}
                        <TableCell className="text-end">
                          <Amount value={row.balance} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={4 + moneyCols} className="font-bold">
                      {t('partners.stClosing')}
                    </TableCell>
                    <TableCell className="text-end text-base font-bold text-primary">
                      <Amount value={statement?.summary?.closingBalance ?? '0.00'} currency={currency} />
                    </TableCell>
                  </TableRow>
                  {showBaseCols ? (
                    <TableRow>
                      <TableCell colSpan={4 + moneyCols} className="font-medium">
                        {t('partners.stClosingBase', { currency: base })}
                      </TableCell>
                      <TableCell className="text-end font-semibold">
                        {statement!.closingBalanceBase === null ? (
                          <span className="text-muted-foreground" title={t('partners.stNoRate')}>
                            —
                          </span>
                        ) : (
                          <Amount value={statement!.closingBalanceBase} currency={base} />
                        )}
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableFooter>
              </Table>
              <div className="no-print">
                <Pager
                  page={page}
                  pages={range.pages}
                  first={range.first}
                  last={range.last}
                  total={range.total}
                  onPage={setPage}
                  label={t('partners.stLedger')}
                />
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
